import { Layout } from 'antd'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import {
  APP_SHELL_CONTENT_ID,
  rememberShellChrome
} from 'src/components/AppLoadingScreen'
import { useSectionTitle, useSessionContext } from 'src/hooks'
import { workspaceSideNavKeyFromPath } from 'src/router'

import { AppFooter } from './AppFooter'
import { useWorkspaceMenu } from './hooks'
import { PageHeader } from './PageHeader'
import { Sidebar } from './Sidebar'
import { useStyles } from './styles'
import { Topbar } from './Topbar'
import type { AppLayoutShellProps } from './types'
import { WorkspaceWelcomeOverlay } from './WorkspaceWelcomeOverlay'

const { Content } = Layout

/**
 * Top-level application chrome rendered for every authenticated route except
 * standalone flows (auth, workspace creation). Provides the workspace switcher,
 * flat workspace sidebar, topbar, and page header slot.
 */
export const AppLayoutShell = ({
  children,
  pageHeader
}: AppLayoutShellProps) => {
  const { styles } = useStyles()
  const [collapsed, setCollapsed] = useState(false)
  const location = useLocation()
  const shellRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const shell = shellRef.current

    return () => rememberShellChrome(shell)
  }, [])
  const { user, activeWorkspaceId, removeUserData } = useSessionContext()

  // Workspace id used by the sidebar. Prefer the URL when on /workspaces/:id/*; otherwise
  // fall back to the session's active workspace.
  const workspaceIdFromPath = useMemo(() => {
    const match = location.pathname.match(/^\/workspaces\/([^/]+)/)
    if (!match) {
      return undefined
    }
    const value = match[1]
    if (!value || value === 'new') {
      return undefined
    }
    const numeric = Number(value)

    return Number.isFinite(numeric) ? numeric : undefined
  }, [location.pathname])

  const workspaceId = workspaceIdFromPath ?? activeWorkspaceId

  const workspaces = useMemo(() => user?.organizations ?? [], [user])

  const menu = useWorkspaceMenu({ workspaceId, user })
  const sectionTitle = useMemo(() => {
    const activeKey = workspaceSideNavKeyFromPath(location.pathname)

    for (const group of menu.groups) {
      const activeItem = group.items.find((item) => item.key === activeKey)

      if (activeItem) {
        return activeItem.label
      }
    }

    return undefined
  }, [location.pathname, menu])

  // Keep the browser title and sidebar highlight on the same route matcher.
  useSectionTitle(sectionTitle)

  return (
    <Layout ref={shellRef} hasSider className={styles.shell}>
      <Sidebar
        collapsed={collapsed}
        onCollapse={setCollapsed}
        menu={menu}
        workspaces={workspaces}
        activeWorkspaceId={workspaceId}
      />
      <Layout>
        <Topbar
          user={user}
          workspaces={workspaces}
          activeWorkspaceId={workspaceId}
          onSignOut={removeUserData}
        />
        <Content id={APP_SHELL_CONTENT_ID} className={styles.content}>
          {pageHeader ? <PageHeader {...pageHeader} /> : null}
          {children}
        </Content>
        <AppFooter />
      </Layout>

      {/* Mounted only once the session has a workspace: the overlay decides
          whether it is owed a welcome on its first render, and mounting it
          against an empty list would spend that decision on nothing. */}
      {workspaces.length > 0 ? (
        <WorkspaceWelcomeOverlay
          workspaces={workspaces}
          activeWorkspaceId={workspaceId}
        />
      ) : null}
    </Layout>
  )
}
